import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import H2 from '@base/H2/H2';
import NextImage from 'next/legacy/image';
import { getImage } from '@utils/imageHelper';
import BreadCrumbs from 'aquilla/core/molecules/Breadcrumbs';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import H3 from '@base/H3/H3';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import P1 from '@base/P1/P1';
import usePlaylists from '@api/hooks/youtube-course/usePlaylists';
import useSinglePlaylists from '@api/hooks/youtube-course/useSinglePlaylists';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import styled from '@emotion/styled';
import CourseList from './CourseList';
import { getYoutubeCourseBreadcrumbs } from './utils';
import CourseCard from './CourseCard';
import TabList from './TabList';

const StyBreadCrumbs = styled(BreadCrumbs)`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const CoursePageWrapper = styled.div`
  grid-column: 1 / -1;
`;

const CourseTypeTitle = styled(H2)`
  margin-bottom: ${Spacings.SPACING_10};
`;

const CourseTypeSubTitle = styled(P1)`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.P1};
  color: var(--color-i-gray-13);
  margin-bottom: ${Spacings.SPACING_3};
`;

const CourseCategoryTitle = styled(H3)`
  padding-top: ${Spacings.SPACING_20};
  margin-bottom: ${Spacings.SPACING_6};
  ${mediaQuery.uptoMobile} {
    margin-left: ${({ $isRenderedInCarousal }) =>
      $isRenderedInCarousal
        ? `-${Spacings.SPACING_15}`
        : `${Spacings.SPACING_4B}`};
  }
`;

const CourseCategorySubTitle = styled(P1)`
  ${mediaQuery.uptoMobile} {
    margin-left: ${({ $isRenderedInCarousal }) =>
      $isRenderedInCarousal
        ? `-${Spacings.SPACING_15}`
        : `${Spacings.SPACING_4B}`};
  }
`;

const CarousalWrapper = styled.div`
  height: auto;
  width: 100%;
  ${mediaQuery.uptoMobile} {
    margin-left: -${Spacings.SPACING_4B};
  }
`;

const FreeCoursesHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_10};
`;

const CoursePage = ({ goalUID }) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { singlePlaylists } = useSinglePlaylists(goalUID);
  const { playlists, size, setSize } = usePlaylists(goalUID);
  const { goalInfo } = useGoalInfo(goalUID);

  const renderCourses = (course) => (
    <CourseCard key={course?.id} course={course} goalUID={goalUID} />
  );

  const onSellAllClick = () =>
    router.push(
      '/content/p/freecourses/[goalUID]/free-lessons',
      `/content/p/freecourses/${router?.query?.goalUID}/free-lessons`
    );

  return (
    <CoursePageWrapper>
      <TabList goalUID={goalUID} />
      <StyBreadCrumbs fragments={getYoutubeCourseBreadcrumbs(goalInfo)} />
      <FreeCoursesHeaderContainer>
        <div>
          <CourseTypeTitle>Free Courses</CourseTypeTitle>
          <CourseTypeSubTitle>
            Learn and prepare important topics with free courses for{' '}
            {goalInfo?.name}
          </CourseTypeSubTitle>
        </div>
        {!isMobile && (
          <NextImage
            src={getImage(
              'youtube-courses/youtube-free-courses-header-img.svg'
            )}
            width={Spacings.SPACING_15B}
            height={Spacings.SPACING_15B}
          />
        )}
      </FreeCoursesHeaderContainer>
      {!!singlePlaylists?.results?.length && (
        <CarousalWrapper>
          <ShowCase
            title={
              <CourseCategoryTitle isRenderedInCarousal>
                Popular classes
              </CourseCategoryTitle>
            }
            itemsPerSlide={4}
            onSeeAll={onSellAllClick}
          >
            {singlePlaylists?.results?.map((course) => (
              <CourseCard
                key={course?.id}
                course={course}
                goalUID={goalUID}
                carousalCard
              />
            ))}
          </ShowCase>
          <CourseCategoryTitle>All courses</CourseCategoryTitle>
          <CourseCategorySubTitle>
            Boost your preparation from curated free courses
          </CourseCategorySubTitle>
        </CarousalWrapper>
      )}
      <CourseList
        goalUID={goalUID}
        renderCourses={renderCourses}
        size={size}
        setSize={setSize}
        playlists={playlists}
      />
    </CoursePageWrapper>
  );
};

export default CoursePage;
